import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  TableContainer,
  CssBaseline,
  Container,
  Card,
  Stack,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { ArrowBack, Search as SearchIcon } from '@mui/icons-material';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

const LIMIT = 10;

const formatTo12Hour = (time) => {
  if (!time) return '';
  const [hour, minute] = String(time).split(':');
  const date = new Date();
  date.setHours(parseInt(hour, 10), parseInt(minute, 10));
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
};

const MEAL_SLOTS = [
  ['Pre-Workout', 'preworkout', 'preworkout_time'],
  ['Post-Workout', 'post_workout', 'post_workout_time'],
  ['Breakfast', 'breakfast', 'breakfast_time'],
  ['Morning Snacks', 'morning_snaks', 'morning_snaks_time'],
  ['Evening Snacks 1', 'evening_snaks1', 'evening_snaks1_time'],
  ['Evening Snacks 2', 'evening_snaks2', 'evening_snaks2_time'],
  ['Dinner', 'dinner', 'dinner_time'],
  ['Meal 1', 'meal1', 'meal1_time'],
  ['Meal 2', 'meal2', 'meal2_time'],
];

const DietAssignUser = () => {
  const { colors: appColors } = useAppTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState([]);
  const [dietPlans, setDietPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [index, setIndex] = useState(0);
  const [dietId, setDietId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedDietPlan, setSelectedDietPlan] = useState(null);

  const getToken = () => JSON.parse(localStorage.getItem('userdata') || '{}')?.token;

  const fetchDietPlans = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${window.constants.api_url}/diet-plans`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ page: 1, limit: 500 }),
      });
      const data = await res.json();
      setDietPlans(data.data || []);
      if (!dietId && (data.data || []).length > 0) {
        setDietId((data.data)[0].id);
      }
    } catch (e) {
      console.error('Error fetching diet plans:', e);
    }
  }, []);

  const fetchAssignments = useCallback(
    async (reset = true) => {
      const token = getToken();
      if (!token) return;
      const nextIndex = reset ? 0 : index;
      if (reset) {
        setLoading(true);
        setAssignments([]);
      } else {
        setLoadingMore(true);
      }
      try {
        const body = {
          limit: LIMIT,
          index: nextIndex,
          diet_id: dietId || undefined,
          search_text: searchText || undefined,
        };
        const res = await fetch(`${window.constants.api_url}/diet-assignments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        const list = Array.isArray(data.data) ? data.data : [];
        const total = data.total ?? list.length;
        if (reset) {
          setAssignments(list);
          setIndex(LIMIT);
        } else {
          setAssignments((prev) => [...prev, ...list]);
          setIndex((i) => i + LIMIT);
        }
        setHasMore(list.length >= LIMIT && (data.total == null || nextIndex + list.length < total));
      } catch (e) {
        console.error('Error fetching diet assignments:', e);
        if (reset) setAssignments([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [index, dietId, searchText]
  );

  useEffect(() => {
    fetchDietPlans();
  }, [fetchDietPlans]);

  useEffect(() => {
    fetchAssignments(true);
  }, [dietId, searchText]);

  const handleSearch = () => {
    setSearchText(searchInput.trim());
  };

  const handleLoadMore = () => {
    fetchAssignments(false);
  };

  const handleDietPlanClick = (row) => {
    const fullPlan = dietPlans.find((d) => d.id === row.diet_id);
    setSelectedDietPlan(fullPlan || row.diet_plan || null);
  };

  const parseDays = (days) => {
    if (!days) return [];
    if (Array.isArray(days)) return days;
    try {
      const parsed = typeof days === 'string' ? JSON.parse(days) : days;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, mt: 8 }}>
        <Container maxWidth="xl">
          {/* Sub-nav: Diet Plans | Assign Diet User */}
          <Card sx={{ p: 1.5, mb: 2, borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant={location.pathname === '/diet-chart' ? 'contained' : 'outlined'}
                onClick={() => navigate('/diet-chart')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  ...(location.pathname !== '/diet-chart' && {
                    color: appColors.evergreen,
                    borderColor: appColors.evergreen,
                    '&:hover': { borderColor: appColors.pine, bgcolor: 'rgba(31, 122, 84, 0.04)' },
                  }),
                }}
              >
                Diet Plans
              </Button>
              <Button
                size="small"
                variant={location.pathname === '/diet-assign-user' ? 'contained' : 'outlined'}
                onClick={() => navigate('/diet-assign-user')}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  ...(location.pathname !== '/diet-assign-user' && {
                    color: appColors.evergreen,
                    borderColor: appColors.evergreen,
                    '&:hover': { borderColor: appColors.pine, bgcolor: 'rgba(31, 122, 84, 0.04)' },
                  }),
                }}
              >
                Assign Diet User
              </Button>
            </Stack>
          </Card>

          <Card sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate(-1)}
                  sx={{
                    color: appColors.evergreen,
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'rgba(31, 122, 84, 0.08)' },
                  }}
                >
                  Back
                </Button>
                <Box>
                  <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 600 }}>
                    Assign Diet User
                  </Typography>
                  <Breadcrumbs sx={{ mt: 0.5 }}>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/diet-chart'); }}>
                      Diet
                    </Link>
                    <Typography color="text.primary">Assign Diet User</Typography>
                  </Breadcrumbs>
                </Box>
              </Stack>
            </Stack>
          </Card>

          {/* Filters */}
          <Card sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              flexWrap="wrap"
            >
              <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
                <InputLabel>Diet Plan</InputLabel>
                <Select
                  value={dietId}
                  onChange={(e) => setDietId(e.target.value)}
                  label="Diet Plan"
                >
                  <MenuItem value="">All</MenuItem>
                  {dietPlans.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.diet_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                placeholder="Search member..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: '100%', sm: 220 } }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{
                  bgcolor: appColors.evergreen,
                  '&:hover': { bgcolor: appColors.pine },
                  textTransform: 'none',
                }}
              >
                Search
              </Button>
            </Stack>
          </Card>

          {/* Table */}
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress sx={{ color: appColors.evergreen }} />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: appColors.evergreen }}>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Member</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Diet Plan</TableCell>
                        {!isMobile && (
                          <>
                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Membership No.</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Phone</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 600 }}>Assigned At</TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={isMobile ? 3 : 6} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No diet assignments found.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        assignments.map((row, i) => (
                          <TableRow
                            key={row.id != null ? row.id : i}
                            sx={{
                              '&:nth-of-type(odd)': { bgcolor: '#f8fafc' },
                              '&:hover': { bgcolor: '#f1f5f9' },
                            }}
                          >
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>
                              {row.member?.name ?? `Member #${row.member_id ?? '-'}`}
                            </TableCell>
                            <TableCell>
                              <Typography
                                component="span"
                                onClick={() => handleDietPlanClick(row)}
                                sx={{
                                  color: appColors.evergreen,
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  '&:hover': { color: appColors.pine },
                                }}
                              >
                                {row.diet_plan?.diet_name ?? '-'}
                              </Typography>
                            </TableCell>
                            {!isMobile && (
                              <>
                                <TableCell>{row.member?.membership_number ?? '-'}</TableCell>
                                <TableCell>{row.member?.phone ?? '-'}</TableCell>
                                <TableCell>
                                  {row.assigned_at
                                    ? new Date(row.assigned_at).toLocaleString(undefined, {
                                        dateStyle: 'short',
                                        timeStyle: 'short',
                                      })
                                    : '-'}
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {hasMore && assignments.length > 0 && (
                  <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      sx={{
                        color: appColors.evergreen,
                        borderColor: appColors.evergreen,
                        '&:hover': { borderColor: appColors.pine, bgcolor: 'rgba(31, 122, 84, 0.04)' },
                        textTransform: 'none',
                      }}
                    >
                      {loadingMore ? <CircularProgress size={24} /> : 'Load more'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Card>

          {/* Diet Plan Detail Dialog */}
          <Dialog
            open={Boolean(selectedDietPlan)}
            onClose={() => setSelectedDietPlan(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
          >
            <DialogTitle sx={{ bgcolor: appColors.evergreen, color: 'white', py: 2 }}>
              {selectedDietPlan?.diet_name ?? 'Diet Plan'}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              {selectedDietPlan && (
                <Stack spacing={2}>
                  {parseDays(selectedDietPlan.days).length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Days
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {parseDays(selectedDietPlan.days).map((day, idx) => (
                          <Chip key={idx} label={day} size="small" sx={{ bgcolor: '#f1f5f9', color: appColors.evergreen }} />
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {MEAL_SLOTS.some(([, key]) => selectedDietPlan[key]) ? (
                    MEAL_SLOTS.map(([label, mealKey, timeKey]) => {
                      const meal = selectedDietPlan[mealKey];
                      if (!meal) return null;
                      return (
                        <Box key={mealKey} sx={{ pb: 1, borderBottom: '1px solid #f1f5f9' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {label}
                          </Typography>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">{meal}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTo12Hour(selectedDietPlan[timeKey])}
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Meal details not available for this plan.
                    </Typography>
                  )}
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setSelectedDietPlan(null)}
                variant="outlined"
                sx={{
                  color: appColors.evergreen,
                  borderColor: appColors.evergreen,
                  '&:hover': { borderColor: appColors.pine, bgcolor: 'rgba(31, 122, 84, 0.04)' },
                  textTransform: 'none',
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default DietAssignUser;
